import { type ReactElement, useCallback } from 'react';

function CodeCard({ filename, children, config }: { filename: string; children: ReactElement; config?: boolean }): ReactElement {
  const handleCopy = useCallback((e: React.MouseEvent) => {
    const btn = e.currentTarget as HTMLButtonElement;
    const pre = btn.parentElement?.parentElement?.querySelector('pre');
    if (pre) {
      void navigator.clipboard.writeText(pre.textContent ?? '');
      btn.textContent = 'copied';
      setTimeout(() => { btn.textContent = 'copy'; }, 1500);
    }
  }, []);

  return (
    <div className="landing-code-card">
      <div className="landing-code-head">
        <div className="landing-code-dots">
          <span /><span /><span />
        </div>
        <div className="landing-code-file landing-mono">{filename}</div>
        <button className="landing-copy-btn landing-mono" onClick={handleCopy}>copy</button>
      </div>
      <pre className="landing-code-body">{children}</pre>
    </div>
  );
}

/* Simple syntax-highlighted code rendering via spans */
function K({ children }: { children: string }) { return <span className="landing-c-k">{children}</span>; }
function S({ children }: { children: string }) { return <span className="landing-c-s">{children}</span>; }
function P({ children }: { children: string }) { return <span className="landing-c-p">{children}</span>; }
function C({ children }: { children: string }) { return <span className="landing-c-c">{children}</span>; }
function F({ children }: { children: string }) { return <span className="landing-c-f">{children}</span>; }
function N({ children }: { children: string }) { return <span className="landing-c-n">{children}</span>; }

export function MCPIntegration(): ReactElement {
  return (
    <section className="landing-mcp-section" id="integration">
      <div className="landing-section-head">
        <div className="landing-section-eyebrow">
          <span className="landing-brk">[</span> 06 · INTEGRATION <span className="landing-brk">]</span>
        </div>
        <h2 className="landing-section-title">
          Two files. <span className="landing-glow-mag">Your agent is live.</span>
        </h2>
        <p className="landing-section-sub">
          gimbal.cloud speaks MCP Streamable HTTP. Any MCP-compatible client
          plugs in. No SDK, no build step, no relay to run yourself.
        </p>
      </div>

      <div className="landing-mcp-grid">
        <CodeCard filename="mcp-config.json">
          <>
            <P>{'{'}</P>
            {'\n  '}<K>"mcpServers"</K>: <P>{'{'}</P>
            {'\n    '}<K>"ue-control"</K>: <P>{'{'}</P>
            {'\n      '}<K>"url"</K>: <S>"https://gimbal.cloud/messages"</S>,
            {'\n      '}<K>"type"</K>: <S>"http"</S>,
            {'\n      '}<K>"headers"</K>: <P>{'{'}</P>
            {'\n        '}<K>"Authorization"</K>:
            {'\n          '}<S>"Bearer gmbl_sk_live_a7f2...c93e"</S>
            {'\n      '}<P>{'}'}</P>
            {'\n    '}<P>{'}'}</P>
            {'\n  '}<P>{'}'}</P>
            {'\n'}<P>{'}'}</P>
          </>
        </CodeCard>

        <CodeCard filename="agent.py · move_actor">
          <>
            <C># 1. find a character in the Warehouse Space</C>
            {'\n'}actors = mcp.<F>find_actors</F>(
            {'\n  '}space_id=<S>"sp_wh_047"</S>,
            {'\n  '}actor_type=<N>1</N>            <C># character</C>
            {'\n'})
            {'\n\n'}<C># 2. claim it (1:1 exclusive on characters)</C>
            {'\n'}mcp.<F>claim_actor</F>(actor_id=actors[<N>0</N>].id)
            {'\n\n'}<C># 3. submit a durable intent — 2s smooth lerp</C>
            {'\n'}result = mcp.<F>move_actor</F>(
            {'\n  '}actor_id=actors[<N>0</N>].id,
            {'\n  '}location={'{'}<S>"x"</S>: <N>1200</N>, <S>"y"</S>: -<N>400</N>, <S>"z"</S>: <N>90</N>{'}'},
            {'\n  '}duration=<N>2000</N>,
            {'\n  '}easing=<S>"ease_in_out"</S>
            {'\n'})
            {'\n'}<C># → intent_result.status == 2 (succeeded)</C>
          </>
        </CodeCard>
      </div>

      <div className="landing-mcp-foot landing-mono">
        <span className="landing-dot cyan" /> Compatible with Claude Code · Codex · Cursor · OpenClaw · any MCP HTTP client
      </div>
    </section>
  );
}
